// Protection+ (US A/B test): the cart drawer's two checkout buttons.
//   accept  — adds the Protection+ variant (once) and goes to checkout
//   decline — takes Protection+ back out if it is in the cart and goes to checkout
// and, on every page of the shop, takes it out of a cart that comes back from checkout.
// The drawer re-renders its contents on every cart change, so clicks are delegated.
(() => {
  if (window.protectionPlusBound) return;
  window.protectionPlusBound = true;

  const PROPERTY = '_protection_plus';

  const cartLinesFor = (cart, variantId) => cart.items.filter((item) => item.variant_id === variantId);

  const goToCheckout = () => {
    window.location.href = '/checkout';
  };

  const track = (choice) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'protection_plus_choice', protection_plus: choice });
  };

  const accept = async (variantId) => {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
    if (cartLinesFor(cart, variantId).length) return;
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1, properties: { [PROPERTY]: 'true' } }] }),
    });
    if (!response.ok) throw new Error(`add failed: ${response.status}`);
  };

  const decline = async (variantId) => {
    const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
    if (!cartLinesFor(cart, variantId).length) return;
    const response = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [variantId]: 0 } }),
    });
    if (!response.ok) throw new Error(`remove failed: ${response.status}`);
  };

  // A customer back from checkout still has Protection+ in the cart, which would put its
  // price in a subtotal above a list that does not show it. Take it out as soon as they are
  // back; the accept button puts it back in on the way to checkout. The drawer is re-rendered
  // the way cart.js clears an upsell-only cart, so it updates without opening.
  // The clean-up still in flight, if any. The checkout buttons wait for it, so a removal that
  // started when the drawer opened cannot land after the accept button has added Protection+.
  let pendingRemoval = Promise.resolve();

  const removeOnReturn = () => {
    pendingRemoval = removeIfInCart();
    return pendingRemoval;
  };

  const removeIfInCart = async () => {
    const variantId = Number(document.querySelector('.cart__protection-plus')?.dataset.variantId);
    if (!variantId) return;
    try {
      const cart = await fetch('/cart.js', { cache: 'no-store' }).then((r) => r.json());
      if (!cartLinesFor(cart, variantId).length) return;
      const drawerItems = document.querySelector('cart-drawer-items');
      const sections = drawerItems?.getSectionsToRender?.().map((s) => s.section) ?? [];
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: { [variantId]: 0 }, sections, sections_url: window.location.pathname }),
      });
      if (!response.ok) throw new Error(`remove failed: ${response.status}`);
      const state = await response.json();
      if (sections.length && state.sections) drawerItems.renderCartState(state);
    } catch (error) {
      console.warn('[Protection+]', error);
    }
  };

  removeOnReturn();
  // The back button can restore the page from the browser cache without running scripts again,
  // and a page loaded from that cache can be handed a stale cart, so check again whenever the
  // drawer opens: that is when the customer actually sees the cart.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) removeOnReturn();
  });
  document.addEventListener('cart-drawer:open', removeOnReturn);

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-protection-plus]');
    if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    const choice = button.dataset.protectionPlus;
    const variantId = Number(button.closest('.cart__protection-plus')?.dataset.variantId);
    track(choice);
    button.setAttribute('aria-busy', 'true');

    try {
      await pendingRemoval;
      if (variantId) {
        await (choice === 'accept' ? accept(variantId) : decline(variantId));
      } else {
        console.warn('[Protection+] no variant configured; continuing to checkout without it');
      }
    } catch (error) {
      // Never block checkout on the add-on.
      console.warn('[Protection+]', error);
    }
    goToCheckout();
  });
})();
